import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { searchService } from '../services/searchService';
import { libraryService } from '../services/libraryService';
import useAppNavigation from '../hooks/useAppNavigation';
import { trackEvent } from '../hooks/usePersonalization';
import { recordSearch, recordEmptySearch } from '../hooks/useProactiveAssist';
import { getQualityTier, qualifiesHiResAudioBadge } from '../hooks/useAudioQuality';
import HiResAudioBadge from './HiResAudioBadge';
import { useServices } from '../providers/ServicesProvider';
import useMicrophone from '../hooks/useMicrophone';
import useTextToSpeech from '../hooks/useTextToSpeech';
import VoiceMeter from './VoiceMeter';
import { ContentSkeleton } from './FormattedContent';
import { useTranslation } from '../i18n/useTranslation';
import { useMaestroStore, useActiveZone } from '../store/maestroStore';
import { isIOSNativeApp } from '../utils/platform';
import { QueueController } from '../transport/QueueController';

const SOURCE_COLORS = {
  qobuz: '#4A90D9',
  presto: '#E85D75',
  local: '#22C55E',
  spotify: '#1DB954',
};

function SourceBadge({ source, sourceLabel, isPreview }) {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
      style={{
        background: `${SOURCE_COLORS[source] || '#888'}22`,
        color: SOURCE_COLORS[source] || '#888',
        border: `1px solid ${SOURCE_COLORS[source] || '#888'}44`,
      }}
    >
      {sourceLabel}{isPreview ? ' (Preview)' : ''}
    </span>
  );
}

function QualityBadge({ bitDepth, sampleRate }) {
  const tier = getQualityTier(bitDepth, sampleRate);
  if (!tier) return null;
  if (qualifiesHiResAudioBadge(bitDepth, sampleRate)) {
    return <HiResAudioBadge bitDepth={bitDepth} sampleRate={sampleRate} height={16} />;
  }
  const detail = [];
  if (bitDepth) detail.push(`${bitDepth}-bit`);
  if (sampleRate) detail.push(`${sampleRate}kHz`);
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold"
      style={{
        background: tier.bg,
        color: tier.color,
        border: tier.border,
      }}
    >
      {tier.label}{detail.length > 0 ? ` ${detail.join('/')}` : ''}
    </span>
  );
}

function ArtistRow({ artist, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(artist)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left"
      style={{ ':hover': {} }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
        {artist.imageUrl ? (
          <img src={artist.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-lg" style={{ color: 'var(--text-secondary)' }}>♪</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{artist.name}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <SourceBadge source={artist.source} sourceLabel={artist.sourceLabel} isPreview={artist.isPreview} />
        </div>
      </div>
    </button>
  );
}

function AlbumRow({ album, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(album)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left touch-manipulation select-none [-webkit-tap-highlight-color:transparent]"
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
        {(album.coverUrlSmall || album.coverUrlThumbnail || album.coverUrl) ? (
          <img src={album.coverUrlSmall || album.coverUrlThumbnail || album.coverUrl} alt="" className="w-full h-full object-cover pointer-events-none" loading="lazy" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-lg pointer-events-none" style={{ color: 'var(--text-secondary)' }}>💿</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{album.title}</div>
        <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{album.artistName}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <SourceBadge source={album.source} sourceLabel={album.sourceLabel} isPreview={album.isPreview} />
          <QualityBadge bitDepth={album.maxBitDepth} sampleRate={album.maxSampleRate} />
        </div>
      </div>
    </button>
  );
}

function PlaylistRow({ playlist, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(playlist)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left touch-manipulation select-none [-webkit-tap-highlight-color:transparent]"
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
        {playlist.imageUrl ? (
          <img src={playlist.imageUrl} alt="" className="w-full h-full object-cover pointer-events-none" loading="lazy" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-lg pointer-events-none" style={{ color: 'var(--text-secondary)' }}>♪</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{playlist.name}</div>
        <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
          {playlist.owner ? `by ${playlist.owner}` : ''}{playlist.trackCount ? ` · ${playlist.trackCount} tracks` : ''}
        </div>
        {playlist.source && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <SourceBadge source={playlist.source} sourceLabel={playlist.sourceLabel} isPreview={playlist.isPreview} />
          </div>
        )}
      </div>
    </button>
  );
}

function TrackRow({ track, onSelect }) {
  const mins = Math.floor(track.duration / 60);
  const secs = Math.floor(track.duration % 60);
  return (
    <button
      type="button"
      onClick={() => onSelect(track)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left"
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
        {track.coverUrl ? (
          <img src={track.coverUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>♫</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{track.title}</div>
        <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{track.artistName} — {track.albumTitle}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <SourceBadge source={track.source} sourceLabel={track.sourceLabel} isPreview={track.isPreview} />
          <QualityBadge bitDepth={track.maxBitDepth} sampleRate={track.maxSampleRate} />
          <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-secondary)' }}>{mins}:{String(secs).padStart(2, '0')}</span>
        </div>
      </div>
    </button>
  );
}

function LocalTrackRow({ track, onSelect }) {
  const dur = track.duration || 0;
  const mins = Math.floor(dur / 60);
  const secs = Math.floor(dur % 60);
  return (
    <button
      type="button"
      onClick={() => onSelect(track)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-left"
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
        {track.artworkUrl ? (
          <img src={track.artworkUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>♫</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{track.title}</div>
        <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{track.artist}{track.album ? ` — ${track.album}` : ''}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <SourceBadge source="local" sourceLabel="Library" />
          {dur > 0 && (
            <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-secondary)' }}>{mins}:{String(secs).padStart(2, '0')}</span>
          )}
        </div>
      </div>
    </button>
  );
}

function SectionHeader({ title, count }) {
  if (!count) return null;
  return (
    <div className="px-3 pt-4 pb-1">
      <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>{title}</span>
      <span className="ml-2 text-[11px]" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>{count}</span>
    </div>
  );
}

function normalizeTitle(t) {
  return (t || '').toLowerCase()
    .replace(/\s*[\(\[][^\)\]]*(?:deluxe|remaster|anniversary|edition|expanded|bonus|version|stereo|mono|hi-?res|24[/-](?:44|48|88|96|176|192))[\)\]]*/gi, '')
    .replace(/\s*[-–]\s*(?:deluxe|remaster|super deluxe).*$/i, '')
    .trim();
}

function resolutionScore(a) {
  const bd = a.maxBitDepth || a.maximum_bit_depth || 16;
  const sr = a.maxSampleRate || a.maximum_sampling_rate || 44.1;
  return bd * 1000 + sr;
}

function deduplicateAlbums(list) {
  const groups = new Map();
  for (const a of list) {
    const key = normalizeTitle(a.title) + '||' + (a.artistName || '').toLowerCase();
    const existing = groups.get(key);
    if (!existing || resolutionScore(a) > resolutionScore(existing.best)) {
      groups.set(key, {
        best: a,
        others: existing ? [...existing.others, existing.best] : [],
      });
    } else {
      existing.others.push(a);
    }
  }
  const primaries = [];
  const otherVersions = [];
  for (const { best, others } of groups.values()) {
    primaries.push(best);
    otherVersions.push(...others);
  }
  return { primaries, otherVersions };
}

function categorizeAlbums(list) {
  if (!list || list.length === 0) return { albums: [], singlesEps: [], compilations: [], otherVersions: [] };
  const rawAlbums = [];
  const singlesEps = [];
  const compilations = [];
  for (const a of list) {
    const type = (a.productType || '').toLowerCase();
    const title = a.title || '';
    const isEpTitle = /\bep\b/i.test(title);
    const isSingleTitle = /\(single\)/i.test(title);
    const isLiveTitle = /\b(live at|live from|live in|live on)\b/i.test(title) || /\(live\)/i.test(title);
    const isCompTitle = /\b(greatest hits|best of|collection|anthology)\b/i.test(title);

    if (type === 'single' || type === 'ep' || isEpTitle || isSingleTitle) {
      singlesEps.push(a);
    } else if (type === 'compilation' || type === 'live' || type === 'soundtrack' || isLiveTitle || isCompTitle) {
      compilations.push(a);
    } else if (!type && (a.trackCount || 0) > 0 && (a.trackCount || 0) <= 3) {
      singlesEps.push(a);
    } else {
      rawAlbums.push(a);
    }
  }

  const { primaries: albums, otherVersions } = deduplicateAlbums(rawAlbums);

  const byNewest = (a, b) => {
    const ya = parseInt(a.releaseDate || a.releasedAt || '') || 0;
    const yb = parseInt(b.releaseDate || b.releasedAt || '') || 0;
    return yb - ya;
  };
  return {
    albums: albums.sort(byNewest),
    singlesEps: singlesEps.sort(byNewest),
    compilations: compilations.sort(byNewest),
    otherVersions: otherVersions.sort(byNewest),
  };
}

function TopResultCard({ topResult, onSelectArtist, onSelectAlbum, t }) {
  if (!topResult) return null;
  const { type, item } = topResult;

  if (type === 'artist') {
    return (
      <div className="px-3 pt-3 pb-1">
        <div className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-secondary)' }}>
          {t('search.top_result') || 'Top Result'}
        </div>
        <button
          type="button"
          onClick={() => onSelectArtist(item)}
          className="w-full flex items-center gap-4 p-3 rounded-xl transition text-left"
          style={{ background: 'var(--bg-secondary)' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-primary)' }}>
            {item.imageUrl ? (
              <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl" style={{ color: 'var(--text-secondary)' }}>♪</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold truncate" style={{ color: 'var(--text-primary)' }}>{item.name}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
                Artist
              </span>
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    );
  }

  if (type === 'album') {
    return (
      <div className="px-3 pt-3 pb-1">
        <div className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-secondary)' }}>
          {t('search.top_result') || 'Top Result'}
        </div>
        <button
          type="button"
          onClick={() => onSelectAlbum(item)}
          className="w-full flex items-center gap-4 p-3 rounded-xl transition text-left"
          style={{ background: 'var(--bg-secondary)' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-primary)' }}>
            {(item.coverUrlSmall || item.coverUrlThumbnail || item.coverUrl) ? (
              <img src={item.coverUrlSmall || item.coverUrlThumbnail || item.coverUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl" style={{ color: 'var(--text-secondary)' }}>💿</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold truncate" style={{ color: 'var(--text-primary)' }}>{item.title}</div>
            <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-secondary)' }}>{item.artistName}</div>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
                Album
              </span>
              <QualityBadge bitDepth={item.maxBitDepth} sampleRate={item.maxSampleRate} />
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    );
  }

  return null;
}

function fuzzyDistance(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = i - 1;
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
}

function rankAndFilterArtists(artists, query) {
  if (!artists || !query) return [];
  const q = query.toLowerCase().trim().replace(/\s+/g, ' ');

  const scored = artists.map((a) => {
    const name = (a.name || '').toLowerCase().trim();
    let score = 0;

    if (name === q) {
      score = 200;
    } else if (name.startsWith(q)) {
      score = 160;
    } else if (name.includes(q)) {
      score = 120;
    } else {
      const maxLen = Math.max(q.length, name.length);
      const dist = fuzzyDistance(q, name);
      const sim = maxLen > 0 ? 1 - dist / maxLen : 0;
      if (sim >= 0.7) score = Math.round(100 * sim);
      else if (sim >= 0.5) score = Math.round(60 * sim);
      else score = 10;
    }

    score += Math.min(a.albumCount || 0, 40);
    return { ...a, _score: score };
  });

  scored.sort((a, b) => b._score - a._score);

  const hasStrongMatch = scored.length > 0 && scored[0]._score >= 120;
  const cap = hasStrongMatch ? 10 : 8;
  return scored.slice(0, cap);
}

// Module-level cache so last search survives slideout close/reopen
let _lastQuery = '';
let _lastResults = null;
let _lastLocalResults = [];
let _lastDiscographyArtist = null;
let _lastDiscographyAlbums = [];

const isIOS = !!(window.__maestroNative || window.webkit?.messageHandlers?.openVoiceSearch);

const SEARCH_HISTORY_KEY = 'maestro_search_history';
const MAX_HISTORY = 10;

function getSearchHistory() {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function addToSearchHistory(q) {
  const trimmed = (q || '').trim();
  if (!trimmed || trimmed.length < 2) return;
  let history = getSearchHistory();
  history = history.filter(h => h.toLowerCase() !== trimmed.toLowerCase());
  history.unshift(trimmed);
  history = history.slice(0, MAX_HISTORY);
  try { localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history)); } catch {}
}

function removeFromHistory(q) {
  let history = getSearchHistory();
  history = history.filter(h => h !== q);
  try { localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history)); } catch {}
}

export default function SearchSlideout({ open, onClose }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(_lastQuery);
  const [results, setResults] = useState(_lastResults);
  const [localResults, setLocalResults] = useState(_lastLocalResults);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceError, setVoiceError] = useState(null);
  const [searchTab, setSearchTab] = useState('all');
  const [searchHistory, setSearchHistory] = useState([]);
  const [discographyArtist, _setDiscographyArtist] = useState(_lastDiscographyArtist);
  const [discographyAlbums, _setDiscographyAlbums] = useState(_lastDiscographyAlbums);
  const [discographyLoading, setDiscographyLoading] = useState(false);
  const setDiscographyArtist = useCallback((v) => { _lastDiscographyArtist = v; _setDiscographyArtist(v); }, []);
  const setDiscographyAlbums = useCallback((v) => { _lastDiscographyAlbums = v; _setDiscographyAlbums(v); }, []);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const searchTimer = useRef(null);
  const historyTimer = useRef(null);
  const doSearchRef = useRef(null);
  const nav = useAppNavigation();
  const activeZone = useActiveZone();
  const { voiceSearchEnabled } = useServices();

  const filterAlbumsForArtist = useCallback((albums, artistName) => {
    if (!albums || !artistName) return [];
    const artistLower = artistName.toLowerCase();
    const matched = albums.filter((a) =>
      (a.artistName || '').toLowerCase().includes(artistLower)
      || artistLower.includes((a.artistName || '').toLowerCase())
    );
    return matched.length > 0 ? matched : albums.slice(0, 30);
  }, []);

  const doSearch = useCallback(async (q, isVoice = false) => {
    if (!q || q.length < 2) {
      setResults(null);
      setLocalResults([]);
      _lastResults = null;
      _lastLocalResults = [];
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const localPromise = libraryService.getTracks({ search: q, limit: 10 })
      .then((resp) => {
        const tracks = resp?.tracks || resp || [];
        const mapped = (Array.isArray(tracks) ? tracks : []).slice(0, 10);
        setLocalResults(mapped);
        _lastLocalResults = mapped;
      })
      .catch(() => {
        setLocalResults([]);
        _lastLocalResults = [];
      });

    try {
      const [data] = await Promise.all([searchService.unified(q, 15), localPromise]);
      setResults(data);
      _lastResults = data;
      _lastQuery = q;
      const streamTotal = (data.results?.artists?.length || 0) + (data.results?.albums?.length || 0) + (data.results?.tracks?.length || 0);
      const localTotal = _lastLocalResults.length;
      const total = streamTotal + localTotal;
      trackEvent('search_performed', { entityType: 'search', entityName: q, metadata: { resultCount: total } });
      if (total > 0) recordSearch(q);
      else recordEmptySearch(q);

    } catch (err) {
      console.error('[SearchSlideout] search error:', err);
      setError('Search failed. Please try again.');
    }
    setLoading(false);
  }, []);
  doSearchRef.current = doSearch;

  const handleVoiceResult = useCallback((transcript) => {
    setVoiceError(null);
    setQuery(transcript);
    _lastQuery = transcript;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (historyTimer.current) clearTimeout(historyTimer.current);
    addToSearchHistory(transcript);
    doSearch(transcript, false);
  }, [doSearch]);

  const handleVoiceError = useCallback((err) => {
    if (err === 'no-speech' || err === 'aborted') return;
    const msg = err === 'not-allowed'
      ? isIOSNativeApp()
        ? 'Microphone access denied. Please allow microphone access when prompted.'
        : 'Microphone access denied. Check your browser permissions.'
      : err === 'audio-capture'
        ? 'No microphone available.'
        : `Voice error: ${err}`;
    setVoiceError(msg);
  }, []);

  const mic = useMicrophone({
    onResult: handleVoiceResult,
    onError: handleVoiceError,
  });

  const { speak, stop: stopSpeech, isSpeaking } = useTextToSpeech({ enabled: voiceSearchEnabled });

  useEffect(() => {
    if (open) {
      setSearchTab('all');
      setQuery(_lastQuery);
      setResults(_lastResults);
      setLocalResults(_lastLocalResults);
      setError(null);
      setVoiceError(null);
      _setDiscographyArtist(_lastDiscographyArtist);
      _setDiscographyAlbums(_lastDiscographyAlbums);
      setSearchHistory(getSearchHistory());
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        resultsRef.current?.scrollTo(0, 0);
      });
    } else {
      mic.stop();
      stopSpeech();
      if (historyTimer.current) clearTimeout(historyTimer.current);
    }
  }, [open]);

  useEffect(() => {
    window.__nativeSearchQuery = (q) => {
      if (!q || !q.trim()) return;
      console.log('[SearchSlideout] __nativeSearchQuery received:', q);
      setQuery(q);
      _lastQuery = q;
      _setDiscographyArtist(null);
      _lastDiscographyArtist = null;
      _setDiscographyAlbums([]);
      _lastDiscographyAlbums = [];
      setResults(null);
      _lastResults = null;
      setLocalResults([]);
      _lastLocalResults = [];
      addToSearchHistory(q.trim());
      if (doSearchRef.current) {
        doSearchRef.current(q, false);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleBackToSearch = useCallback(() => {
    setDiscographyArtist(null);
    setDiscographyAlbums([]);
    requestAnimationFrame(() => {
      resultsRef.current?.scrollTo(0, 0);
    });
  }, []);

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    if (discographyArtist) {
      setDiscographyArtist(null);
      setDiscographyAlbums([]);
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(val), 200);
    if (historyTimer.current) clearTimeout(historyTimer.current);
    historyTimer.current = setTimeout(() => {
      if (val && val.trim().length >= 2) addToSearchHistory(val.trim());
    }, 1500);
  }, [doSearch, discographyArtist, setDiscographyArtist, setDiscographyAlbums]);

  const handleSelectArtist = useCallback((artist) => {
    const quickAlbums = filterAlbumsForArtist(results?.results?.albums, artist.name);
    setDiscographyArtist(artist);
    setDiscographyAlbums(quickAlbums);
    setDiscographyLoading(quickAlbums.length === 0);
    searchService.unified(artist.name, 50).then((data) => {
      const allAlbums = data?.results?.albums || [];
      const artistLower = (artist.name || '').toLowerCase();
      const matched = allAlbums.filter((a) =>
        (a.artistName || '').toLowerCase().includes(artistLower)
        || artistLower.includes((a.artistName || '').toLowerCase())
      );
      const full = matched.length > 0 ? matched : allAlbums.slice(0, 30);
      setDiscographyAlbums(full);
      setDiscographyLoading(false);
    }).catch(() => setDiscographyLoading(false));
  }, [filterAlbumsForArtist, results, setDiscographyArtist, setDiscographyAlbums]);

  const handleSelectAlbum = useCallback((album) => {
    onClose();
    nav.openAlbum(album);
  }, [nav, onClose]);

  const handleSelectPlaylist = useCallback((playlist) => {
    onClose();
    nav.openPlaylist({ id: playlist.servicePlaylistId || playlist.id, title: playlist.name });
  }, [nav, onClose]);

  const handleSelectTrack = useCallback((track) => {
    onClose();
    nav.openAlbum({
      id: track.albumId,
      serviceAlbumId: track.albumId,
      title: track.albumTitle,
      artistName: track.artistName,
      coverUrl: track.coverUrl,
      source: track.source,
    });
  }, [nav, onClose]);

  const handleSelectLocalTrack = useCallback((track) => {
    onClose();
    QueueController.playAll([{
      id: track.id || `local_search_${Date.now()}`,
      title: track.title || 'Unknown',
      artist: track.artist || track.artistName || '',
      album: track.album || '',
      artworkUrl: track.artworkUrl || '',
      duration: track.duration || 0,
      source: 'local_library',
      format: String(track.format || '').toUpperCase() || 'FLAC',
      file_path: track.file_path,
      machine_id: track.machine_id,
    }]);
  }, [onClose]);

  const rankedArtists = useMemo(() => {
    return rankAndFilterArtists(results?.results?.artists, query);
  }, [results, query]);

  const topResult = results?.topResult || null;
  const suggestion = results?.suggestion || null;
  const rawAlbums = results?.results?.albums || [];
  const { primaries: albums } = useMemo(() => {
    const result = deduplicateAlbums(rawAlbums);
    result.primaries.sort((a, b) => {
      const ya = parseInt(a.releaseDate || a.releasedAt || '') || 0;
      const yb = parseInt(b.releaseDate || b.releasedAt || '') || 0;
      return yb - ya;
    });
    return result;
  }, [rawAlbums]);
  const tracks = results?.results?.tracks || [];
  const playlists = results?.results?.playlists || [];

  const hasResults = localResults.length > 0 || rankedArtists.length > 0 || albums.length > 0 || tracks.length > 0 || playlists.length > 0 || topResult;
  const noResults = results && !hasResults && query.length >= 2;

  return (
    <>
      {/* Scrim */}
      {open && (
        <div
          className="fixed inset-0 z-[120] transition-opacity"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
          onClick={onClose}
          onTouchEnd={(e) => { e.preventDefault(); onClose(); }}
        />
      )}

      {/* Slideout panel — above MiniPlayer (z-110) when open; when closed, no hit target (iOS). */}
      <div
        className={`fixed top-0 right-0 z-[121] h-full flex flex-col transition-transform duration-250 ease-out ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{
          width: 'min(420px, 100vw)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border, rgba(0,0,0,0.08))',
          boxShadow: open ? '-8px 0 30px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}>
          {isIOS ? (
            <div className="flex-1 text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Search</div>
          ) : (
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-secondary)' }}>
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                data-search-input="true"
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                placeholder={t('search.placeholder')}
                className="w-full rounded-xl py-2.5 pl-9 pr-10 text-sm focus:outline-none"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border, rgba(0,0,0,0.08))',
                  color: 'var(--text-primary)',
                }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              {query.length > 0 && !mic.isListening && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    _lastQuery = '';
                    setResults(null);
                    _lastResults = null;
                    setLocalResults([]);
                    _lastLocalResults = [];
                    setDiscographyArtist(null);
                    setDiscographyAlbums([]);
                    requestAnimationFrame(() => inputRef.current?.focus());
                  }}
                  className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center transition"
                  style={{ right: mic.isSupported ? 32 : 8, color: 'var(--text-secondary)' }}
                  title="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
              {mic.isSupported && (
                <button
                  type="button"
                  onClick={() => {
                    if (mic.isListening || mic.isPreparing) {
                      mic.stop();
                    } else {
                      setQuery('');
                      _lastQuery = '';
                      setResults(null);
                      _lastResults = null;
                      setLocalResults([]);
                      _lastLocalResults = [];
                      setDiscographyArtist(null);
                      setDiscographyAlbums([]);
                      mic.start();
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition"
                  style={{ color: mic.isListening ? '#C9A84C' : 'var(--text-secondary)' }}
                  title={mic.isListening ? t('search.stop_listening') : t('search.voice_search')}
                >
                  {mic.isListening ? (
                    <VoiceMeter active={true} barCount={4} height={18} />
                  ) : mic.isPreparing ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-pulse" style={{ opacity: 0.5 }}>
                      <rect x="9" y="1" width="6" height="13" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="1" width="6" height="13" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center transition"
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Category filter tabs — All | Albums | Artists | Tracks | Playlists */}
        <div
          className="flex gap-1.5 px-3 pt-2 pb-2 overflow-x-auto flex-shrink-0"
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', borderBottom: '1px solid var(--border, rgba(128,128,128,0.12))' }}
        >
          {['all', 'albums', 'artists', 'tracks', 'playlists'].map((key) => {
            const labels = {
              all: t('search.all'),
              albums: t('search.albums'),
              artists: t('search.artists'),
              tracks: t('search.tracks'),
              playlists: t('search.playlists'),
            };
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSearchTab(key)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                style={{
                  background: searchTab === key ? 'rgba(100,130,255,0.18)' : 'var(--bg-secondary)',
                  color: searchTab === key ? '#7c8aff' : 'var(--text-secondary)',
                  border: searchTab === key ? '1px solid rgba(100,130,255,0.3)' : '1px solid transparent',
                }}
              >
                {labels[key]}
              </button>
            );
          })}
        </div>

        {/* Results */}
        <div ref={resultsRef} className="flex-1 overflow-y-auto no-scrollbar ios-scroll pb-24 search-results-container">
          {/* Search history — shown when query is empty and no results */}
          {(!query || query.length < 2) && !results && !discographyArtist && searchHistory.length > 0 && (
            <div className="px-3 pt-3">
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Recent searches</span>
                <button
                  type="button"
                  onClick={() => {
                    try { localStorage.removeItem(SEARCH_HISTORY_KEY); } catch {}
                    setSearchHistory([]);
                  }}
                  className="text-xs px-2 py-0.5 rounded transition"
                  style={{ color: 'var(--text-secondary)', background: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                >
                  Clear all
                </button>
              </div>
              {searchHistory.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center group rounded-lg transition"
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <button
                    type="button"
                    className="flex-1 flex items-center gap-2.5 px-3 py-2.5 text-left text-sm"
                    style={{ color: 'var(--text-primary)', background: 'none', border: 'none' }}
                    onClick={() => {
                      setQuery(item);
                      _lastQuery = item;
                      if (searchTimer.current) clearTimeout(searchTimer.current);
                      if (historyTimer.current) clearTimeout(historyTimer.current);
                      addToSearchHistory(item);
                      doSearch(item);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span className="truncate">{item}</span>
                  </button>
                  <button
                    type="button"
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded transition opacity-0 group-hover:opacity-100"
                    style={{ color: 'var(--text-secondary)', background: 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromHistory(item);
                      setSearchHistory(prev => prev.filter(h => h !== item));
                    }}
                    title="Remove"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Discography view — shown after voice match or artist tap */}
          {discographyArtist && (
            <div>
              <div className="px-4 pt-3 pb-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isIOS && window.__maestroNative?.openVoiceSearch) {
                      window.__maestroNative.openVoiceSearch();
                    } else {
                      handleBackToSearch();
                      setQuery(''); _lastQuery = '';
                      setResults(null); _lastResults = null;
                      setLocalResults([]); _lastLocalResults = [];
                      requestAnimationFrame(() => inputRef.current?.focus());
                    }
                  }}
                  className="flex items-center justify-center gap-2 w-full rounded-[10px] text-[15px] font-semibold text-white"
                  style={{ padding: '11px 16px', background: 'linear-gradient(135deg, #4d80ff, #7c4dff)', border: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  New Search
                </button>
              </div>

              {/* Artist header */}
              {(() => {
                const cat = categorizeAlbums(discographyAlbums);
                const albumCount = cat.albums.length;
                return (
                  <>
                    <div className="flex items-center gap-4 px-4 pt-2 pb-3">
                      <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'var(--bg-secondary)' }}>
                        {discographyArtist.imageUrl ? (
                          <img src={discographyArtist.imageUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl" style={{ color: 'var(--text-secondary)' }}>♪</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-lg font-bold truncate" style={{ color: 'var(--text-primary)' }}>{discographyArtist.name}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {discographyLoading ? 'Loading…' : `${albumCount} album${albumCount !== 1 ? 's' : ''}${cat.singlesEps.length > 0 ? ` · ${cat.singlesEps.length} single${cat.singlesEps.length !== 1 ? 's' : ''}/EP${cat.singlesEps.length !== 1 ? 's' : ''}` : ''}`}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            nav.openArtist(discographyArtist.id, discographyArtist.name);
                          }}
                          className="mt-1.5 text-xs font-medium py-1 px-2.5 rounded-md transition"
                          style={{ color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)' }}
                        >
                          {t('search.view_artist') || 'View full profile'}
                        </button>
                      </div>
                    </div>

                    {/* Categorized album list */}
                    {discographyLoading && discographyAlbums.length === 0 ? (
                      <div className="px-4"><ContentSkeleton lines={10} /></div>
                    ) : discographyAlbums.length > 0 ? (
                      <>
                        {discographyLoading && (
                          <div className="mx-4 mb-2 h-0.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-secondary)' }}>
                            <div className="h-full rounded-full animate-pulse" style={{ width: '60%', background: '#C9A84C', opacity: 0.5 }} />
                          </div>
                        )}
                        {cat.albums.length > 0 && (
                          <>
                            <SectionHeader title="Albums" count={cat.albums.length} />
                            {cat.albums.map((a, i) => (
                              <AlbumRow key={`disc-alb-${a.source}-${a.id}-${i}`} album={a} onSelect={handleSelectAlbum} />
                            ))}
                          </>
                        )}
                        {cat.singlesEps.length > 0 && (
                          <>
                            <SectionHeader title="Singles & EPs" count={cat.singlesEps.length} />
                            {cat.singlesEps.map((a, i) => (
                              <AlbumRow key={`disc-sep-${a.source}-${a.id}-${i}`} album={a} onSelect={handleSelectAlbum} />
                            ))}
                          </>
                        )}
                        {cat.compilations.length > 0 && (
                          <>
                            <SectionHeader title="Compilations & Live" count={cat.compilations.length} />
                            {cat.compilations.map((a, i) => (
                              <AlbumRow key={`disc-comp-${a.source}-${a.id}-${i}`} album={a} onSelect={handleSelectAlbum} />
                            ))}
                          </>
                        )}
                        {cat.otherVersions && cat.otherVersions.length > 0 && (
                          <>
                            <SectionHeader title="Other Versions" count={cat.otherVersions.length} />
                            {cat.otherVersions.map((a, i) => (
                              <AlbumRow key={`disc-ov-${a.source}-${a.id}-${i}`} album={a} onSelect={handleSelectAlbum} />
                            ))}
                          </>
                        )}
                      </>
                    ) : (
                      <div className="px-4 pt-4 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                        No albums found.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Voice listening indicator — only on desktop when mic is genuinely capturing */}
          {!isIOS && !discographyArtist && mic.isListening && (
            <div className="px-4 pt-3 pb-2 flex items-center gap-3">
              <VoiceMeter active={true} barCount={5} height={28} />
              <div className="flex-1 min-w-0">
                <div className="text-xs mb-0.5" style={{ color: 'var(--text-secondary)' }}>{t('search.listening')}</div>
                {mic.interimTranscript && (
                  <div className="text-sm italic truncate" style={{ color: 'var(--text-primary)', opacity: 0.7 }}>{mic.interimTranscript}</div>
                )}
              </div>
            </div>
          )}

          {!discographyArtist && voiceError && (
            <div className="px-4 pt-2 text-xs text-red-500">{voiceError}</div>
          )}

          {!discographyArtist && loading && !results && (
            <div className="px-4 pt-2">
              <ContentSkeleton lines={8} />
            </div>
          )}

          {!discographyArtist && error && (
            <div className="px-4 pt-4 text-center text-sm text-red-500">{error}</div>
          )}

          {!discographyArtist && noResults && (
            <div className="px-4 pt-8 text-center">
              <div className="text-lg mb-2" style={{ color: 'var(--text-secondary)' }}>{t('search.no_results')}</div>
              <div className="text-sm mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>Try a different search term or check your connected services.</div>
              {isIOS && (
                <button
                  type="button"
                  onClick={() => window.__maestroNative?.openVoiceSearch?.()}
                  className="inline-flex items-center justify-center gap-2 rounded-[10px] text-[15px] font-semibold text-white"
                  style={{ padding: '11px 24px', background: 'linear-gradient(135deg, #4d80ff, #7c4dff)', border: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  Try Again
                </button>
              )}
            </div>
          )}

          {!discographyArtist && hasResults && (
            <>
              <div className="px-4 pt-1 pb-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.__maestroNative?.openVoiceSearch) {
                      window.__maestroNative.openVoiceSearch();
                    } else {
                      setQuery(''); _lastQuery = '';
                      setResults(null); _lastResults = null;
                      setLocalResults([]); _lastLocalResults = [];
                      setDiscographyArtist(null); setDiscographyAlbums([]);
                      requestAnimationFrame(() => inputRef.current?.focus());
                    }
                  }}
                  className="flex items-center justify-center gap-2 w-full rounded-[10px] text-[15px] font-semibold text-white"
                  style={{ padding: '11px 16px', background: 'linear-gradient(135deg, #4d80ff, #7c4dff)', border: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  New Search
                </button>
              </div>
              {suggestion && (
                <div className="px-4 pt-3 pb-1">
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {t('search.showing_results_for') || 'Showing results for'}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setQuery(suggestion.correctedQuery);
                        if (historyTimer.current) clearTimeout(historyTimer.current);
                        addToSearchHistory(suggestion.correctedQuery);
                        doSearch(suggestion.correctedQuery);
                      }}
                      className="font-bold underline underline-offset-2 transition-colors"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {suggestion.correctedQuery}
                    </button>
                  </div>
                </div>
              )}
              {(searchTab === 'all' || searchTab === 'albums') && (
                <TopResultCard topResult={topResult} onSelectArtist={handleSelectArtist} onSelectAlbum={handleSelectAlbum} t={t} />
              )}

              {(searchTab === 'all') && localResults.length > 0 && (
                <>
                  <SectionHeader title="Library" count={localResults.length} />
                  {localResults.map((tr, i) => (
                    <LocalTrackRow key={`local-${tr.id || i}`} track={tr} onSelect={handleSelectLocalTrack} />
                  ))}
                </>
              )}
              {(searchTab === 'all' || searchTab === 'artists') && rankedArtists.length > 0 && (
                <>
                  <SectionHeader title={t('search.artists')} count={rankedArtists.length} />
                  {rankedArtists.map((a, i) => (
                    <ArtistRow key={`${a.source}-${a.id}-${i}`} artist={a} onSelect={handleSelectArtist} />
                  ))}
                </>
              )}
              {(searchTab === 'all' || searchTab === 'albums') && albums.length > 0 && (
                <>
                  <SectionHeader title={t('search.albums')} count={albums.length} />
                  {albums.map((a, i) => (
                    <AlbumRow key={`${a.source}-${a.id}-${i}`} album={a} onSelect={handleSelectAlbum} />
                  ))}
                </>
              )}
              {(searchTab === 'all' || searchTab === 'tracks') && tracks.length > 0 && (
                <>
                  <SectionHeader title={t('search.tracks')} count={tracks.length} />
                  {tracks.map((tr, i) => (
                    <TrackRow key={`${tr.source}-${tr.id}-${i}`} track={tr} onSelect={handleSelectTrack} />
                  ))}
                </>
              )}
              {(searchTab === 'all' || searchTab === 'playlists') && playlists.length > 0 && (
                <>
                  <SectionHeader title={t('search.playlists')} count={playlists.length} />
                  {playlists.map((pl, i) => (
                    <PlaylistRow
                      key={`${pl.source}-${pl.id}-${i}`}
                      playlist={pl}
                      onSelect={handleSelectPlaylist}
                    />
                  ))}
                </>
              )}
              <div className="px-4 pt-6 pb-6">
                <button
                  type="button"
                  onClick={() => {
                    if (window.__maestroNative?.openVoiceSearch) {
                      window.__maestroNative.openVoiceSearch();
                    } else {
                      setQuery(''); _lastQuery = '';
                      setResults(null); _lastResults = null;
                      setLocalResults([]); _lastLocalResults = [];
                      setDiscographyArtist(null); setDiscographyAlbums([]);
                      requestAnimationFrame(() => inputRef.current?.focus());
                    }
                  }}
                  className="flex items-center justify-center gap-2 w-full rounded-[10px] text-[15px] font-semibold"
                  style={{ padding: '11px 16px', background: 'transparent', border: '1px solid rgba(128,128,255,0.3)', color: '#7c8aff', WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  New Search
                </button>
              </div>
            </>
          )}

          {!discographyArtist && !loading && !results && query.length === 0 && !mic.isListening && !mic.isPreparing && (
            isIOS ? (
              <div className="flex flex-col items-center justify-center" style={{ padding: '60px 20px', gap: '16px' }}>
                <button
                  type="button"
                  onClick={() => window.__maestroNative?.openVoiceSearch?.()}
                  className="flex items-center justify-center gap-2 rounded-xl text-[16px] font-semibold text-white"
                  style={{ padding: '14px 32px', background: 'linear-gradient(135deg, #4d80ff, #7c4dff)', border: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  Search Music
                </button>
                <p className="text-sm m-0" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>Search by voice or text</p>
              </div>
            ) : (
              <div className="px-4 pt-8 text-center">
                <div className="text-4xl mb-3" style={{ opacity: 0.3 }}>🔍</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('search.empty_title')}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
                  {mic.isSupported && voiceSearchEnabled
                    ? 'Tap the mic or type to search'
                    : 'Type to find artists, albums, and tracks'}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}
